import { askWhyRefused } from "./api.ts";

// A server-sent event stream that stays open while the page does. The browser reconnects by itself
// when a connection ends or drops (every deploy ends them all: features/live-updates.md, Gotchas), but
// gives up for good when a reconnect is answered with an error, such as the proxy's 502 while a new
// server takes longer than usual to start. Then this opens it again: after 1 s, then waiting twice as
// long each time, up to 30 s, until one connects. `url` is asked each time, so a stream can say where
// to pick up. `listen` adds the handlers to each new connection; `stop` (also returned) closes it for good.
// A stream refused because the Terms changed meanwhile brings the screen up (api.ts, askWhyRefused), which
// takes the page's place and so closes it.
export function keepOpen(url: () => string, listen: (source: EventSource, stop: () => void) => void): () => void {
  const FIRST_MS = 1000;
  const MAX_MS = 30_000;
  let wait = FIRST_MS;
  let stopped = false;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let source: EventSource | null = null;
  const stop = () => {
    stopped = true;
    clearTimeout(timer);
    source?.close();
  };
  const open = () => {
    const current = new EventSource(url());
    source = current;
    current.addEventListener("open", () => {
      wait = FIRST_MS;
    });
    current.addEventListener("error", () => {
      if (stopped || current.readyState !== EventSource.CLOSED) return;
      void askWhyRefused();
      timer = setTimeout(open, wait);
      wait = Math.min(wait * 2, MAX_MS);
    });
    listen(current, stop);
  };
  open();
  return stop;
}
